"use client";
import {
  ObjectDetailsType,
  SearchOptionsType,
  MultiIndexSearchType,
  AlgoliaSingleIndexType,
  GlobalSearchResults,
  IRecentSearch,
} from "@/utils/types/algolia";
import { algoliaConfig } from "./config";
import { algoliaClient } from "@dxp-feed/algolia";
import { getGuestUserRandomId } from "@/helpers/global";
import {
  FALLBACK_POPULAR_SEARCH_ITEMS,
  LOCAL_STORAGE_KEYS,
  RECIPES_ARTICLES_DIETARY_PREFERENCE_MAPPING,
  SEARCH_DROPDOWN_MAX_QUICK_LINKS_RESULTS_COUNT,
  USER_DIETARY_PREFERENCE_KEYS,
} from "@/utils/constant";
import { encryptObject, generateNonce } from "@/helpers/noncehelper";
import { hasProfanity } from "@dxp-feed/algolia/dist/services/profanity";

export const algolia = algoliaClient(algoliaConfig.appId, algoliaConfig.apiKey);

/**
 * @author
 * @description Search store by location
 * @param param
 * {
      lat:50.905561, 
      lng:-114.11181,
      indexName:"dev_sobeys_stores",  // ["dev_sobeys_stores","dev_sobeys_products_en"]
      query :"Sobeys Riverbend", //optional 
      page:0, // optional
      hitsPerPage: 10,
    }
 * @returns 
 */

export const searchStoresByLocation = async ({
  lat,
  lng,
  indexName = "Store",
  query = "",
  page = 0,
  hitsPerPage = 20,
  userToken,
}: SearchOptionsType): Promise<any> => {
  userToken = userToken ?? getGuestUserRandomId();
  try {
    const result = await algolia.storeSearchByLatLng({
      lat,
      lng,
      indexName,
      query,
      page,
      hitsPerPage,
      userToken,
      facets: ["departments.type", "address.province"],
    });

    return result;
  } catch (error) {
    console.error("Error searching stores by location:", error);
    throw new Error("Failed to search stores by location.");
  }
};

/**
 * @author
 * @description Fetch data by category ID, recipe ID, etc.
 * @param payload Array of objects containing the `objectID` and `indexName` for the search.
 * Example payload:[{
 *   objectID: "97433",    // product id, recipe id, etc.
 *   indexName: "dev_sobeys_recipes_en",   // e.g., dev_sobeys_recipes_en, dev_sobeys_products_en
 * }]
 *
 */

export const fetchDataFromAlgoliaById = async (
  payload: ObjectDetailsType[]
): Promise<any[]> => {
  try {
    const response = (await algolia.getObjectDetails(payload)) as any[];
    return response;
  } catch (error) {
    console.error("Error fetching data from Algolia:", error);
    throw new Error("Failed to fetch data from Algolia.");
  }
};

export const searchByMultiIndex = async (
  query: MultiIndexSearchType[]
): Promise<any> => {
  try {
    const results = await algolia.multiIndexSearch(query);
    return results;
  } catch (error) {
    console.error("Error during multi-index search:", error);
    throw new Error("Failed to perform multi-index search.");
  }
};

export const searchBySingleIndex = async (
  query: AlgoliaSingleIndexType,
  userToken?: string
): Promise<any> => {
  try {
    const { indexName, searchParams } = query;
    const filters = searchParams?.filters ?? "";
    userToken ??= getGuestUserRandomId();
    const filterValuePattern = /(?:^|\s)([^:\s]+):([^\s]+)/g;
    const filterMap = new Map<string, string>();
    let match;
    while ((match = filterValuePattern.exec(filters)) !== null) {
      filterMap.set(match[1], match[2]);
    }
    const isValid =
      indexName === algoliaConfig.indexNames.products
        ? filterMap.get("storeId") &&
          filterMap.get("isVisible") === "true" &&
          filterMap.get("isMassOffers") === "false"
        : indexName === algoliaConfig.indexNames.recipes ||
            indexName === algoliaConfig.indexNames.articles
          ? !!filterMap.get("bannerCode")
          : true;

    return isValid
      ? await algolia.search({
          indexName,
          searchParams: { ...searchParams, userToken },
        })
      : { hits: [] };
  } catch (error) {
    console.error("Error during search:", error);
    throw new Error("Failed to perform search.");
  }
};

export const fetchPopularSearch = async () => {
  try {
    const url = `${process.env.NEXT_PUBLIC_BASE_URL}/api/algolia/popularSearches`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-nonce-token": generateNonce(),
      },
    });
    if (!res.ok) {
      throw new Error(`Popular Searches API failed with status ${res.status}`);
    }
    const response = await res.json();
    if (
      response?.response &&
      Array.isArray(response?.response) &&
      response?.response.length
    ) {
      return response?.response;
    } else {
      console.warn("Unexpected popular search data format.");
      return FALLBACK_POPULAR_SEARCH_ITEMS;
    }
  } catch (error) {
    console.error("Failed to fetch popular searches:", error);
    return FALLBACK_POPULAR_SEARCH_ITEMS;
  }
};

export const fetchRecomendSearchResults = async ({
  storeID,
  filter,
}: {
  storeID?: string;
  filter?: {
    product?: string;
    recipe?: string;
    article?: string;
  };
}) => {
  try {
    const url = `${process.env.NEXT_PUBLIC_BASE_URL}/api/algolia/trendingItems`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-nonce-token": generateNonce(),
      },
      body: JSON.stringify({
        storeId: storeID,
        filter,
      }),
    });
    if (!res.ok) {
      throw new Error(`TrendingItems API failed with status ${res.status}`);
    }
    const response = await res.json();
    if (response?.data && typeof response?.data === "object") {
      return {
        products: response?.data?.products ?? null,
        recipes: response?.data?.recipes ?? null,
        articles: response?.data?.articles ?? null,
      };
    } else {
      console.warn("Unexpected trendingItems response format.", response);
      return null;
    }
  } catch (error) {
    console.error("Failed to fetch recommendations via API:", error);
    return null;
  }
};

export const fetchGlobalSearchResults = async ({
  query = "",
  userToken,
  filter,
  shouldFetchQuerySuggestions = false,
  optionalFilters,
}: {
  query?: string;
  userToken?: string;
  filter?: {
    product?: string;
    recipe?: string;
    article?: string;
  };
  shouldFetchQuerySuggestions?: boolean;
  optionalFilters?: {
    product?: string[];
    recipe?: string[];
    article?: string[];
  };
}) => {
  try {
    userToken = userToken ?? getGuestUserRandomId();
    const response = (await algolia.multiIndexSearch(
      [
        {
          indexName: algoliaConfig.indexNames.products,
          query,
          hitsPerPage: 30,
          clickAnalytics: true,
          userToken,
          filters: filter?.product,
          optionalFilters: optionalFilters?.product,
          analyticsTags: [process.env.NEXT_PUBLIC_BANNER],
        },
        {
          indexName: algoliaConfig.indexNames.recipes,
          query,
          hitsPerPage: 30,
          clickAnalytics: true,
          userToken,
          filters: filter?.recipe,
          optionalFilters: optionalFilters?.recipe,
        },
        {
          indexName: algoliaConfig.indexNames.articles,
          query,
          hitsPerPage: 30,
          clickAnalytics: true,
          userToken,
          filters: filter?.article,
          optionalFilters: optionalFilters?.article,
        },
        shouldFetchQuerySuggestions
          ? {
              indexName: algoliaConfig.indexNames.productQuerySuggestion,
              query,
              hitsPerPage: 5,
              clickAnalytics: true,
              userToken,
            }
          : null,
      ].filter((item) => !!item)
    )) as { results: Array<unknown> };

    if (Array.isArray(response.results) && response.results.length) {
      return {
        products: response.results[0],
        recipes: response.results[1],
        articles: response.results[2],
        querySuggestion: shouldFetchQuerySuggestions
          ? response.results[3]
          : undefined,
      } as GlobalSearchResults;
    } else {
      console.warn("Unexpected global search data format.");
      return null;
    }
  } catch (error) {
    console.error("Failed to fetch global searches:", error);
    return null;
  }
};

export const fetchRecentSearches = async (
  userToken: string
): Promise<IRecentSearch[]> => {
  let recentSearches: IRecentSearch[] = [];
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.RECENT_SEARCH);
    if (stored) {
      recentSearches = JSON.parse(stored) as IRecentSearch[];
    }
  } catch (err) {
    console.error("Failed to parse recent searches from localStorage:", err);
  }
  if (userToken) {
    try {
      const url = `${process.env.NEXT_PUBLIC_BASE_URL}/api/algolia/recentSearches`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${encryptObject({ encrypt: crypto.randomUUID() }).toUpperCase()}`,
          focus: `${userToken}`,
          "x-nonce-token": generateNonce(),
        },
      });
      if (res.ok) {
        const response = await res.json();
        return response?.response ?? [];
      } else {
        console.error(
          "Failed to fetch recent searches from server:",
          await res.text()
        );
      }
    } catch (err) {
      console.error("Error fetching recent searches from server:", err);
    }
  }
  return recentSearches;
};

export const addRecentSearches = async ({
  searchTerm,
  userToken,
  recentSearches,
}: {
  searchTerm: string;
  userToken: string;
  recentSearches: IRecentSearch[];
}): Promise<IRecentSearch[]> => {
  if (hasProfanity(searchTerm)) {
    return recentSearches;
  }
  const uniqueId = Date.now() + Math.floor(Math.random() * 1000);
  const filteredSearches = recentSearches.filter(
    (item) => item.label.toLowerCase() !== searchTerm.toLowerCase()
  );
  const newEntry: IRecentSearch[] = [
    { id: uniqueId.toString(), label: searchTerm },
    ...filteredSearches,
  ].slice(0, SEARCH_DROPDOWN_MAX_QUICK_LINKS_RESULTS_COUNT);

  if (userToken) {
    try {
      const url = `${process.env.NEXT_PUBLIC_BASE_URL}/api/algolia/recentSearches`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${encryptObject({ encrypt: crypto.randomUUID() }).toUpperCase()}`,
          focus: `${userToken}`,
          "x-nonce-token": generateNonce(),
        },
        body: JSON.stringify({ items: newEntry }),
      });
      if (!res.ok) {
        console.error(
          "Failed to sync recent searches with server:",
          await res.text()
        );
      }
    } catch (err) {
      console.error("Error posting recent searches to server:", err);
    }
  } else {
    try {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.RECENT_SEARCH,
        JSON.stringify(newEntry)
      );
    } catch (err) {
      console.error("Failed to save recent searches to localStorage:", err);
    }
  }
  return newEntry;
};

export const deleteRecentSearch = async ({
  id,
  userToken,
  recentSearches,
}: {
  id: string;
  userToken: string;
  recentSearches: IRecentSearch[];
}): Promise<IRecentSearch[]> => {
  const updatedSearches = recentSearches.filter((item) => item.id !== id);

  if (userToken) {
    try {
      const url = `${process.env.NEXT_PUBLIC_BASE_URL}/api/algolia/recentSearches`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${encryptObject({ encrypt: crypto.randomUUID() }).toUpperCase()}`,
          focus: `${userToken}`,
          "x-nonce-token": generateNonce(),
        },
        body: JSON.stringify({ items: updatedSearches }),
      });
      if (!res.ok) {
        console.error(
          "Failed to sync updated recent searches with server:",
          await res.text()
        );
      }
    } catch (err) {
      console.error("Error syncing deleted search to server:", err);
    }
  } else {
    try {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.RECENT_SEARCH,
        JSON.stringify(updatedSearches)
      );
    } catch (err) {
      console.error(
        "Failed to save updated recent searches to localStorage:",
        err
      );
    }
  }

  return updatedSearches;
};

export const getPayloadForAlgolia = (
  recipeListData: any
): { indexName: string; objectID: string }[] => {
  const payload = [
    ...new Set(
      recipeListData.flatMap((list: { recipes: any[] }) =>
        list.recipes.map((r: any) => r.recipe_id)
      )
    ),
  ].map((id) => ({
    indexName: algoliaConfig.indexNames.recipes,
    objectID: id as string,
  }));

  return payload;
};

export const getEnrichList = (recipeListData: any, data: any) => {
  const filteredData = data?.results.filter(Boolean);
  const enrichList = recipeListData.map((list: { recipes: any[] }) => ({
    ...list,
    recipes: list.recipes.map((r: any) => {
      const enriched = filteredData.find(
        (d: any) => d.objectID === r.recipe_id
      );
      return {
        ...r,
        ...(enriched || {}),
      };
    }),
  }));
  return enrichList;
};

export const FetchDietaryPreferencesResults = async (
  storeID: string,
  searchQuery: string,
  lifestyles: Record<string, boolean>
) => {
  const selectedPreferences = USER_DIETARY_PREFERENCE_KEYS.filter(
    (key) => lifestyles?.[key]
  );
  if (!selectedPreferences.length) return null;
  const bannerCode = `bannerCode:${process.env.NEXT_PUBLIC_DEFAULT_BANNER}`;
  try {
    const userToken = getGuestUserRandomId();
    const response = (await algolia.multiIndexSearch(
      [
        {
          indexName: algoliaConfig.indexNames.products,
          query: searchQuery,
          hitsPerPage: 30,
          clickAnalytics: true,
          userToken,
          filters: `storeId:"${storeID}" AND allergens.alcohol:false AND isVisible:true AND isMassOffers:false AND (${selectedPreferences
            .map((pref) => `allergens.${pref}:true`)
            .join(" OR ")})`,
          analyticsTags: [process.env.NEXT_PUBLIC_BANNER],
        },
        {
          indexName: algoliaConfig.indexNames.recipes,
          query: searchQuery,
          hitsPerPage: 30,
          clickAnalytics: true,
          userToken,
          filters: `${bannerCode} AND (${selectedPreferences
            .map(
              (pref) =>
                `recipeTags:'${RECIPES_ARTICLES_DIETARY_PREFERENCE_MAPPING[pref]}'`
            )
            .join(" OR ")})`,
        },
        {
          indexName: algoliaConfig.indexNames.articles,
          query: searchQuery,
          hitsPerPage: 30,
          clickAnalytics: true,
          userToken,
          filters: `${bannerCode} AND (${selectedPreferences
            .map(
              (pref) =>
                `category:'${RECIPES_ARTICLES_DIETARY_PREFERENCE_MAPPING[pref]}'`
            )
            .join(" OR ")})`,
        },
      ].filter((item) => !!item)
    )) as { results: Array<unknown> };

    if (Array.isArray(response.results) && response.results.length) {
      return {
        products: response.results[0],
        recipes: response.results[1],
        articles: response.results[2],
      } as GlobalSearchResults;
    } else {
      console.warn("Unexpected global search data format.");
      return null;
    }
  } catch (error) {
    console.error("Failed to fetch global searches:", error);
    return null;
  }
};
